import { Buffer } from 'buffer';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { IProfileFieldConstraintsMap, IWorkerCustomField } from 'types';

//* Email regex sourced from https://uibakery.io/regex-library/email which claims to be RFC 5322 compliant
export const EMAIL_REGEX =
  /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
export const ZIP_CODE_REGEX = new RegExp(/^[0-9]{5}-[0-9]{4}$|^[0-9]{5}$|^[A-Z][0-9][A-Z]-[0-9][A-Z][0-9]$/);
export const NAME_REGEX = /^([a-zA-Z\s])*$/;
export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[\d\W])(?!.*\s)[A-Za-z\d\W]{8,15}$/;

export const getFontBaseValue = (defaultFontSize = 16): number => {
  return defaultFontSize;
};

export const toREM = (value: number): string => {
  const baseValue = getFontBaseValue();
  return `${value * (1 / baseValue)}rem`;
};

export const isObject = (entity: any) => entity && typeof entity === 'object' && entity.constructor === Object;

export const isArray = (value: any) => Array.isArray(value);

export const isUndefined = (value: any) => value === undefined;

export const isNull = (value: any) => value === null;

export const isNumber = (value: any) => typeof value === 'number';

export const isString = value => typeof value === 'string';

export const isEmpty = value => {
  if (isObject(value)) {
    return Object.keys(value).length === 0;
  }
  if (isUndefined(value) || isNull(value)) {
    return true;
  }
  if (isArray(value)) {
    return value.length === 0;
  }
  if (isNumber(value)) {
    return Number.isNaN(value);
  }
  if (isString(value)) {
    return value.trim() === '';
  }

  return value === '';
};

/**
 * Parse URL query parameters while preserving special characters
 */
export const parseQueryParams = (search: string): URLSearchParams => {
  const encodedSearch = search.replace(/\+/g, '%2B');
  return new URLSearchParams(encodedSearch);
};

// export const isEmail = (email) =>
//   /^\w+([\+.-]?\w+)*@\w+([\+.-]?\w+)*(\.\w{2,3})+$/.test(email);

export const isEmail = email => EMAIL_REGEX.test(email);

export const isPhoneNo = phoneNo => phoneNo && phoneNo.length > 10;

export const isValidMobileNumber = mobileNumber => isValidPhoneNumber(mobileNumber);

export const isZipCodeValid = zipCode => ZIP_CODE_REGEX.test(zipCode);

// Yup test config for a minimum length that ignores blank values. Yup's own `.min()` also fails a
// blank value, and useForm keeps the last error per field, so a blank required field would show the
// min message instead of the required one. Usage: `.test(minLengthIfFilled(2, message))`.
export const minLengthIfFilled = (min: number, message: string) => ({
  name: 'min',
  message,
  test: (value?: string | null) => !value || value.length >= min,
});

export const createGuid = () => {
  let u = Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
  let guid = [u.substr(0, 8), u.substr(8, 4), '4000-8' + u.substr(13, 3), u.substr(16, 12)].join('-');
  return guid;
};

export const injectScript = ({ src, scriptId }: { src: string; scriptId: string }) => {
  if (document.getElementById(scriptId)) return Promise.resolve(document.getElementById(scriptId));
  return new Promise(function (resolve, reject) {
    const script = document.createElement('script');
    script.src = src;
    script.id = scriptId;
    script.type = 'text/javascript';
    script.onload = /* istanbul ignore next */ () => resolve(script);
    script.onerror = /* istanbul ignore next */ () => reject(new Error(`Script load error for ${src}`));
    document.body.append(script);
  });
};

export const disableFocus = () => {
  document.body.classList.add('using-mouse');
  return true;
};

export const enableFocus = () => {
  document.body.classList.remove('using-mouse');
  return true;
};

export const enableFocusOnTab = (event: any) => {
  return event.keyCode === 9 ? enableFocus() : false;
};

export const getDefaultValue = (value: any, defaultValue: any = '-') => (isEmpty(value) ? defaultValue : value);

export const getConditionalDefaultValue = (condition: any, value: any, defaultValue: any = '-') =>
  !!condition ? getDefaultValue(value, defaultValue) : defaultValue;

export const getDarkColor = () => {
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += Math.floor(Math.random() * 10);
  }
  return color;
};

export const parseJwt = (token: string) => {
  var base64Payload = token.split('.')[1];
  var payload = Buffer.from(base64Payload, 'base64');
  return JSON.parse(payload.toString());
};

export const percentage = (partialValue, totalValue) => {
  return (100 * partialValue) / totalValue;
};

export const isMobile = (mobileScreenWidth?: number) => {
  return window.innerWidth <= (mobileScreenWidth || 600);
};

const getNestedObject = (nestedObj = {}, path: string = '') => {
  return path.split('.').reduce((obj, key) => (obj[key] !== 'undefined' ? obj[key] : undefined), nestedObj);
};

export const getDataFromObjectUsingPaths = (nestedObj, paths: string | string[] = '') => {
  if (typeof paths === 'object' && paths?.constructor === Array) {
    let data = {};
    paths?.forEach(path => {
      const pathArray = path.split('.');
      data = { ...data, [pathArray.pop()]: getNestedObject(nestedObj, path) };
    });
    return data;
  }
  return getNestedObject(nestedObj, paths as string);
};

export const canCreateUnverifiedWorkersInProject = (workerProfileCustomFields: any) => {
  let result = true;
  if (
    !workerProfileCustomFields?.length ||
    workerProfileCustomFields.filter(
      wcf => wcf.isVisible === null && wcf.isMandatory === null && wcf.isSkippable === null,
    ).length === workerProfileCustomFields.length
  ) {
    result = false;
  } else {
    const piiFieldCodes = [
      'WorkerDateOfBirth',
      'WorkerLastSSN',
      'WorkerHomeAddressLine1',
      'WorkerHomeAddressLine2',
      'WorkerHomeAddressCountry',
      'WorkerHomeAddressStateProvince',
      'WorkerHomeAddressCity',
      'WorkerHomeAddressZipPostal',
    ];
    workerProfileCustomFields?.forEach(item => {
      if (
        piiFieldCodes.includes(item.code) &&
        ((item.isVisible === null && item.isVisibleByDefault) || item.isVisible)
      ) {
        result = false;
      }
    });
  }
  return result;
};

export const getProfileFieldConstraintsMap = (fields: IWorkerCustomField[]): IProfileFieldConstraintsMap => {
  const constraintsMap: IProfileFieldConstraintsMap = {};

  fields.forEach(field => {
    const isVisible = field.isVisible || (field.isVisible === null && field.isVisibleByDefault);
    const isMandatory = isVisible && (field.isMandatory || (field.isMandatory === null && field.isMandatoryByDefault));
    const isSkippable =
      isMandatory && (field.isSkippable || (field.isSkippable === null && field.isSkippableByDefault));
    constraintsMap[field.code] = {
      isVisible,
      isMandatory,
      isSkippable,
      skippableTill: isSkippable ? (field.skippableTill ?? 0) : 0,
    };
  });
  return constraintsMap;
};

// TODO: Refactor transformErrorResponse code
export const transformErrorResponse = (error: any) => {
  if (!error) return null;

  const { data } = error?.response || {};
  if (data?.errors) {
    return data.errors;
  } else if (data) {
    return data;
  } else {
    return error;
  }
};

// TODO: Refactor prettifyErrorResponse code
export const prettifyErrorResponse = (error: any, ignoreLengthCheck = false, separator = ', ') => {
  let defaultMessage = 'Something went wrong. Please try again.';
  let errorMessage = '';
  if (!error) return defaultMessage;

  const { data } = error?.response || {};
  if (data?.errors) {
    errorMessage = Object.values(data.errors)
      .map((err: any) => (typeof err === 'string' ? err : err?.join(' ')))
      .join(separator);
  } else if (data?.message || data?.title) {
    errorMessage = data?.message || data?.title;
  } else if (error?.message || error?.title) {
    errorMessage = error?.message || error?.title;
  } else if (isObject(error)) {
    errorMessage = Object.values(error)
      .map((err: any) => (typeof err === 'string' ? err : err?.join(' ')))
      .join(separator);
  } else if (Array.isArray(error)) {
    errorMessage = error
      .map((err: any) => (typeof err === 'string' ? err : prettifyErrorResponse(err, ignoreLengthCheck)))
      .join(separator);
  } else if (isString(error)) {
    errorMessage = error;
  } else {
    errorMessage = defaultMessage;
  }
  return errorMessage?.length > 300 && !ignoreLengthCheck ? defaultMessage : errorMessage;
};

/**
 * Formats backend validation errors from the new error format
 * Extracts all error messages and combines them with line breaks
 * @param errorResponse - The error response from the API (can be the errors object directly or wrapped in { errors: {...} })
 * @returns Formatted error message with line breaks
 */
export const formatBackendValidationErrors = (errorResponse: any): string => {
  if (!errorResponse || typeof errorResponse !== 'object') {
    return prettifyErrorResponse(errorResponse);
  }

  // Check if errorResponse directly contains error arrays (like workerCustomFieldValues[0].value: [...])
  // or if it's wrapped in an errors property
  const errors = errorResponse.errors || errorResponse;
  const errorMessages: string[] = [];

  // Extract all error messages from the errors object
  Object.values(errors).forEach((errorArray: any) => {
    if (Array.isArray(errorArray)) {
      errorArray.forEach(msg => {
        if (typeof msg === 'string' && msg.trim()) {
          errorMessages.push(msg.trim());
        }
      });
    } else if (typeof errorArray === 'string' && errorArray.trim()) {
      errorMessages.push(errorArray.trim());
    }
  });

  // Join with line breaks if we have messages, otherwise use default
  return errorMessages.length > 0 ? errorMessages.join('\n') : 'Validation failed. Please check your input.';
};

export const isMobileDevice = ({
  iPadAsMobile,
  androidTabletAsMobile,
}: { iPadAsMobile?: boolean; androidTabletAsMobile?: boolean } = {}): boolean => {
  if (typeof navigator === 'undefined') return false;

  // 1. Chromium: userAgentData (safe guarded)
  const uaData = (navigator as any).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    // true for phones; false for desktops/tablets on Chromium. [web:6][web:34]
    if (uaData.mobile) return true;

    // uaData.mobile === false here: could be desktop OR tablet.
    // If caller wants Android tablets treated as "mobile", we need a UA-based tablet check.
    if (androidTabletAsMobile) {
      const ua = navigator.userAgent || '';
      const isAndroidTabletByUA = /Android/i.test(ua) && !/Mobile/i.test(ua); // Google’s classic heuristic: Android + not "Mobile" ⇒ tablet. [web:27][web:45][web:48]

      if (isAndroidTabletByUA) return true;
    }

    // Otherwise treat as non-mobile.
    return false;
  }

  const ua = navigator.userAgent || '';

  // 2. Quick UA sniff for common *phone* patterns (covers Safari/Firefox mobile etc.)
  const mobileRegex = /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i;
  // "Android.*Mobile" tries to avoid matching Android tablets that lack "Mobile". [web:27][web:58]
  if (mobileRegex.test(ua)) {
    return true;
  }

  // 3. Treat Android tablets as "mobile" via UA + flag
  if (androidTabletAsMobile) {
    const isAndroidTabletByUA = /Android/i.test(ua) && !/Mobile/i.test(ua); // Android present, but not "Mobile" → usually tablet. [web:27][web:45][web:59]
    if (isAndroidTabletByUA) {
      return true;
    }

    // 3.5. Viewport/touch sanity check for edge cases for Android tablets
    const hasTouch = 'maxTouchPoints' in navigator ? navigator.maxTouchPoints > 0 : 'ontouchstart' in window; // Generic touch heuristic. [web:23][web:52]

    const isLargeViewport =
      typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 769px)').matches; // Larger than typical phone breakpoint. [web:23]

    // Fallback: Android + large screen + touch ⇒ likely tablet.
    const isAndroid = /Android/i.test(ua);
    if (isAndroid && hasTouch && isLargeViewport) {
      return true;
    }
  }

  // 4. Treat iPad and similar large touch devices as "mobile" UX-wise
  const isIPad = /\b(iPad)\b/i.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1); // New iPadOS identifies as Mac with touch. [web:31]

  if (iPadAsMobile && isIPad) {
    return true;
  }

  return false;
};

export const getBackendErrorsFromResponse = (
  error: any,
  fields: string[],
  errorFieldMapper?: { [key: string]: string },
) => {
  const backendErrors: Record<string, { type: string; message: string }> = {};

  fields.forEach(field => {
    const mappedField = errorFieldMapper?.[field] || field;
    if (Array.isArray(error[mappedField])) {
      backendErrors[field] = {
        type: 'backendError',
        message: error[mappedField].join(' '),
      };
    }
  });

  if (Object.keys(backendErrors).length > 0) {
    return backendErrors;
  }
  return null;
};
